import { m, AnimatePresence, LazyMotion, domAnimation } from 'framer-motion';
import React, { useEffect, useState, ReactNode, useRef } from 'react';

import { usePayment } from '../Context/PaymentContext';

interface AccordionProps {
    ariaControls: string;
    title: string;
    children: ReactNode;
    defaultopen?: boolean;
    singleOpenMode?: boolean;
    addressMode?: boolean;
    id?: number;
    setMode: React.Dispatch<React.SetStateAction<string>>;
    setMode2?: React.Dispatch<React.SetStateAction<string>>;
    onChange?: (id: number, newState: boolean) => void;
    stateFromParent?: boolean;
    accordionStates;
    setAccordionStates;
}

export const AccordionButton = ({
    children,
    title,
    ariaControls,
    defaultopen = false,
    id,
    setMode,
    setMode2,
    singleOpenMode, //only one accordion open at a time
    addressMode, // address page has 2 independant view modes rather than one
    onChange,
    accordionStates,
    setAccordionStates,
    stateFromParent,
}: AccordionProps) => {
    const [isOpen, setIsOpen] = useState(defaultopen);
    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setIsOpen(stateFromParent);
    }, [stateFromParent]);

    const toggleAccordion = () => {
        if (singleOpenMode) {
            const newState = !isOpen;
            setIsOpen(newState);
            onChange(id, newState);
        } else if (addressMode) {
            // normally we simply toggle the accordion state, however
            // we have to intervene.  depending on if were currently editing an address
            // in another accordion.
            if (id == 0) {
                setMode('view');
                setMode2('view');
                if (isOpen) {
                    updateAccordionAtIndex(id, false);
                } else {
                    updateAccordionAtIndex(id, true);
                }
            } else if (id == 1) {
                // add address toggle has specific rules regarding other accordions
                setMode('view');
                setMode2('view');
                if (isOpen) {
                    updateAccordionAtIndex(1, false);
                } else {
                    updateAccordionAtIndex(1, true);
                }
            } else if (id == 2) {
                // add address toggle has specific rules regarding other accordions
                setMode('view');
                setMode2('view');
                if (isOpen) {
                    updateAccordionAtIndex(2, false);
                } else {
                    updateAccordionAtIndex(0, true);
                    updateAccordionAtIndex(1, true);
                    updateAccordionAtIndex(2, true);
                }
            }
        } else {
            // normally we simply toggle the accordion state, however
            // we have to intervene.  depending on if were currently editing a CC
            // in another accordion.

            if (id == 1) {
                // add card toggle has specific rules regarding other accordions
                setMode('view');
                if (isOpen) {
                    updateAccordionAtIndex(1, false);
                } else {
                    updateAccordionAtIndex(0, true);
                    updateAccordionAtIndex(1, true);
                }
            } else {
                // other accordions such as manage and giftcard just toggle open/shut
                toggleAccordionAtIndex(id);
                return;
            }
        }
    };

    function updateAccordionAtIndex(index, isOpen) {
        if (index >= 0 && index < accordionStates.length) {
            const updatedAccordionState = [...accordionStates];
            updatedAccordionState[index].isOpen = isOpen;
            setAccordionStates(updatedAccordionState);
        } else {
            console.log('Invalid index.');
        }
    }

    function toggleAccordionAtIndex(index) {
        if (index >= 0 && index < accordionStates.length) {
            const updatedAccordionState = [...accordionStates];
            updatedAccordionState[index].isOpen = !updatedAccordionState[index].isOpen;
            setAccordionStates(updatedAccordionState);
        } else {
            console.log('Invalid index.');
        }
    }

    return (
        <div>
            <button
                className="accordion__button"
                onClick={toggleAccordion}
                aria-expanded={isOpen}
                aria-controls={ariaControls}
                type="button"
                id={`b${ariaControls}`}
            >
                <span className="accordion__button--text">{title}</span>
                {isOpen ? <div className="accordion__icon--minus" /> : <div className="accordion__icon--plus" />}
            </button>

            <AnimatePresence initial={false}>
                {isOpen && (
                    <LazyMotion features={domAnimation}>
                        <m.div
                            ref={divRef}
                            initial={{ opacity: 1, height: 0 }}
                            animate={{ opacity: 1, height: 'initial' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            onAnimationStart={() => {
                                if (divRef.current) {
                                    divRef.current.style.overflow = 'hidden';
                                }
                            }}
                            onAnimationComplete={() => {
                                if (divRef.current) {
                                    divRef.current.style.overflow = 'initial';
                                }
                            }}
                            style={{ overflow: 'hidden' }}
                            id={ariaControls}
                            role="region"
                            aria-labelledby={`b${ariaControls}`}
                        >
                            {children}
                        </m.div>
                    </LazyMotion>
                )}
            </AnimatePresence>
        </div>
    );
};
